import React, { useMemo, useState } from 'react';
import { AopLink, Card, InternalCard, Pill, WarnRow } from '../ui';
import { deepResearch } from '../copData';
import { exportJson, stamp } from '../copExport';
import { useCop } from '../CopApp';

const EXAMPLES = [
	'Which campaign bets should we prioritize if margin pressure worsens and mobile checkout abandonment keeps rising?',
	'What should we tell an executive who wants more growth without increasing discount cost?',
	'Which test results should change next-quarter planning, and which signals are not strong enough yet?',
];

function CitationList({ ids, sourcesById }) {
	return (
		<span className="cop-citations">
			{ids.map((id) => {
				const source = sourcesById[id];
				return (
					<span className="cop-cite" title={source?.title || id} key={id}>
						{id}
					</span>
				);
			})}
		</span>
	);
}

export default function DeepResearch() {
	const { showToast, recordAudit, isInternal, reportDateRange, reportDateRangeLabel } = useCop();
	const [question, setQuestion] = useState(deepResearch.default_question);
	const [selectedGroups, setSelectedGroups] = useState(() => deepResearch.source_groups.map((g) => g.key));
	const [reportQuestion, setReportQuestion] = useState(deepResearch.default_question);
	const [isRunning, setIsRunning] = useState(false);

	const sourcesById = useMemo(
		() => Object.fromEntries(deepResearch.sources.map((source) => [source.id, source])),
		[]
	);
	const visibleSources = useMemo(
		() => deepResearch.sources.filter((source) => selectedGroups.includes(source.group)),
		[selectedGroups]
	);

	const toggleGroup = (key) => {
		setSelectedGroups((prev) => {
			if (prev.includes(key) && prev.length === 1) return prev;
			return prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
		});
	};

	const runResearch = () => {
		const cleanQuestion = question.trim() || deepResearch.default_question;
		setQuestion(cleanQuestion);
		setIsRunning(true);
		setTimeout(() => {
			setReportQuestion(cleanQuestion);
			setIsRunning(false);
			recordAudit({
				actor_type: 'ai_agent',
				actor: 'research_bot',
				action: 'generated deep research report',
				entity: cleanQuestion.slice(0, 48),
				entity_type: 'report',
				source: 'dashboard',
			});
			showToast('Deep research report generated');
		}, 520);
	};

	const exportReport = () => {
		exportJson(`cop-deep-research-${stamp()}.json`, {
			generated_at: new Date().toISOString(),
			question: reportQuestion,
			date_range: reportDateRange,
			date_range_label: reportDateRangeLabel,
			source_filters: selectedGroups,
			report: deepResearch.report,
			sources: deepResearch.sources,
		});
		showToast('Deep research report downloaded (JSON)');
	};

	return (
		<main className="cop-page">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 280 }}>
					<h1 className="cop-page-title">Deep Research</h1>
					<div className="cop-page-sub">{reportDateRangeLabel} · full answer with cited evidence and source freshness</div>
				</div>
				<button className="cop-btn" onClick={exportReport}>Download Report</button>
				<button className="cop-btn primary" onClick={runResearch} disabled={isRunning}>
					{isRunning ? 'Researching...' : 'Run Deep Research'}
				</button>
			</div>

			<div className="cop-research-layout">
				<div className="cop-research-main">
					<Card title="Research Question" icon="◬" right={`${visibleSources.length} visible sources`}>
						<label className="cop-research-question">
							<span>Question</span>
							<textarea value={question} onChange={(e) => setQuestion(e.target.value)} />
						</label>
						<div className="cop-example-row">
							{EXAMPLES.map((example) => (
								<button className="cop-chip" key={example} onClick={() => setQuestion(example)}>
									{example}
								</button>
							))}
						</div>
					</Card>

					<Card title="Full Report" icon="✦" right={isRunning ? 'compiling evidence' : 'ready'}>
						<div className={`cop-research-report${isRunning ? ' loading' : ''}`}>
							<div className="cop-report-question">{reportQuestion}</div>
							<div className="cop-report-answer">{deepResearch.report.answer}</div>

							<div className="cop-research-section-list">
								{deepResearch.report.sections.map((section) => (
									<section className="cop-research-section" key={section.title}>
										<h4>
											{section.title}
											<CitationList ids={section.sources} sourcesById={sourcesById} />
										</h4>
										<p>{section.body}</p>
									</section>
								))}
							</div>
						</div>
					</Card>

					<Card title="Evidence Trail" icon="▤" right="claim -> finding -> source">
						<div className="cop-table-wrap">
						<table className="cop-table">
							<thead>
								<tr>
									<th>Claim</th>
									<th>Finding</th>
									<th>Sources</th>
								</tr>
							</thead>
							<tbody>
								{deepResearch.report.evidence.map((row) => (
									<tr key={row.claim}>
										<td><b>{row.claim}</b></td>
										<td style={{ color: 'var(--text-sec)' }}>{row.finding}</td>
										<td><CitationList ids={row.sources} sourcesById={sourcesById} /></td>
									</tr>
								))}
							</tbody>
						</table>
						</div>
					</Card>

					<div className="cop-grid cop-research-two-col">
						<Card title="Next Actions" icon="◇">
							{deepResearch.report.next_actions.map((action) => (
								<div className="cop-check-row" key={action}>
									<span style={{ color: 'var(--green)' }}>✓</span>
									{action}
								</div>
							))}
						</Card>
						<Card title="Research Risks" icon="!" className="raise">
							{deepResearch.report.risks.map((risk) => (
								<WarnRow key={risk} level="warn">{risk}</WarnRow>
							))}
						</Card>
					</div>
				</div>

				<aside className="cop-research-side">
					<Card title="Source Filters" icon="◫">
						<div className="cop-source-filter">
							{deepResearch.source_groups.map((group) => (
								<button
									key={group.key}
									className={`cop-source-toggle${selectedGroups.includes(group.key) ? ' active' : ''}`}
									onClick={() => toggleGroup(group.key)}
								>
									<span>{group.label}</span>
									<Pill tone="dim">{group.count}</Pill>
								</button>
							))}
						</div>
					</Card>

					<Card title="Sources" icon="§">
						<div className="cop-source-list">
							{visibleSources.map((source) => (
								<div className="cop-source-card" key={source.id}>
									<div className="cop-source-head">
										<Pill tone="purple">{source.id}</Pill>
										<span>{source.freshness}</span>
									</div>
									<h4>{source.title}</h4>
									<p>{source.scope}</p>
									<div>{source.supports}</div>
								</div>
							))}
						</div>
					</Card>

					{isInternal && (
						<InternalCard title="Research Pipeline" icon="⚙" right="research_bot">
							<WarnRow level="ok">Source freshness checked before report assembly.</WarnRow>
							<WarnRow level="ok">Every recommendation has at least one cited source.</WarnRow>
							<WarnRow level="warn">External web sources are disabled in this fixture until connection policies are approved.</WarnRow>
							<div className="cop-aop-links">
								<AopLink view="workflow-runs">research_bot runs</AopLink>
								<AopLink view="reports">research reports</AopLink>
							</div>
						</InternalCard>
					)}
				</aside>
			</div>
		</main>
	);
}
